import type { CategoryFilterState } from '@modules-rocco/filters/filter-state/category-filter-state';
import type { SingleValueFilterState } from '@modules-rocco/filters/filter-state/single-value-filter-state';
import type { RangeFilterState } from '@modules-rocco/filters/filter-state/range-filter-state';
import type { MultipleValuesFilterState } from '@modules-rocco/filters/filter-state/multiple-values-filter-state';
import type { SearchableListFilterState } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import type { SearchQueryFilterState } from '@modules-rocco/filters/filter-state/search-query-filter-state';
import type { LocationFilterState } from '@modules-rocco/filters/filter-state/location-filter-state';
import type { SortOption } from '@rocco/types/enum/sort-option';
import type { VisibilityType } from '@modules/search';

export interface FilterStates {
    visibilityType: VisibilityType;
    shouldUpdateUserPreferences?: boolean;
    totalCount?: number;
    categoryLink?: string;
    sortBy?: SortOption;

    facet?: {
        facetOptions?: FacetOptionFilterState[];
    };

    // DONE: moved all the filter states under its own property
    states: {
        search?: SearchQueryFilterState;

        // product, project and professional
        category?: CategoryFilterState;

        // product only
        isPurchasable?: SingleValueFilterState;
        isOnSale?: SingleValueFilterState;
        priceRange?: RangeFilterState;

        isInStock?: SingleValueFilterState;
        isDeliveryAvailable?: SingleValueFilterState;
        isSampleAvailable?: SingleValueFilterState;

        colors?: MultipleValuesFilterState;

        materials?: SearchableListFilterState;

        lengthRange?: RangeFilterState;
        widthRange?: RangeFilterState;
        heightRange?: RangeFilterState;
        diameterRange?: RangeFilterState;
        weightRange?: RangeFilterState;
        brands?: SearchableListFilterState;
        designers?: SearchableListFilterState;

        downloads?: SearchableListFilterState;

        // product and project
        professionals?: SearchableListFilterState;

        // product and professional; flat list of selected award values —
        // the institution grouping is presentational only
        awards?: SearchableListFilterState;

        // project only
        hasProducts?: SingleValueFilterState;
        hasVideos?: SingleValueFilterState;
        projectOfTheMonth?: SingleValueFilterState;

        // project & professional
        hasAwards?: SingleValueFilterState;
        professions?: SearchableListFilterState;

        // professional only
        projectCount?: MultipleValuesFilterState<number>;
        businessFocuses?: MultipleValuesFilterState;
        searchLocation?: LocationFilterState;

        // professional detail product page only
        libraryRanges?: SearchableListFilterState;
        associations?: SearchableListFilterState;

        // professional case-studies tab only: single-choice project owner
        // (our projects / projects using our products / all)
        viewProjectsBy?: MultipleValuesFilterState;

        professionalCategories?: SearchableListFilterState;
        invalidLink?: SingleValueFilterState;
        noCategory?: SingleValueFilterState;
        disabled?: SingleValueFilterState;
        profileOnly?: SingleValueFilterState;
    };
}

export interface FacetOptionFilterState {
    id: string;
    name: string;
    isGeneral: boolean;
    state: SearchableListFilterState<number>;
    showSearch: boolean;
}

export type FilterStatesKey = keyof FilterStates['states'];

// export type SearchListKeys = Extract<
//     FilterStatesKey,
//     | 'professionals'
//     | 'professions'
//     | 'materials'
//     | 'libraryRanges'
//     | 'associations'
//     | 'brands'
//     | 'designers'
//     | 'professionalCategories'
//     | 'downloads'
// >;

////////////

// export const NON_STATE_FILTER_KEYS = [
//     'shouldUpdateUserPreferences',
//     'totalCount',
//     'categoryLink',
//     'category',
//     'sortBy',
//     'facetURLOptions',
// ] as const;

// type NonFilterStatesKey = (typeof NON_STATE_FILTER_KEYS)[number];

export const SingleValueFilterStateKeys = [
    'isPurchasable',
    'isOnSale',
    'isInStock',
    'isDeliveryAvailable',
    'isSampleAvailable',
    'hasProducts',
    'hasVideos',
    'hasAwards',
    'projectOfTheMonth',
    'invalidLink',
    'noCategory',
    'disabled',
    'profileOnly',
];

export const MultipleValueFilterStateKeys = [
    'colors',
    'businessFocuses',
    'projectCount',
    'viewProjectsBy',
];

export const MultipleValueFilterStateKeysNumeric = ['projectCount'];

export const SearchableListValueFilterStateKeys = [
    'professionals',
    'materials',
    'libraryRanges',
    'associations',
    'professions',
    'brands',
    'designers',
    'professionalCategories',
    'downloads',
    'awards',
];

// TODO: refactor, needs to be included to `MultipleValueFilterStateKeys` and processed properly
export const SingleChoiceKeys = ['projectCount', 'viewProjectsBy'];

export const RangeValueFilterStateKeys = [
    'priceRange',
    'lengthRange',
    'widthRange',
    'heightRange',
    'diameterRange',
    'weightRange',
];

export const LocationFilterStateKeys = ['searchLocation'];
